import { useLocation, useNavigate } from 'react-router';
import { useCallback, useMemo } from 'react';

/**
 * Custom hook for managing URL search parameters
 * Provides utilities to read and update query parameters while preserving current path
 */
export const useUrlSearchParams = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Parse current search params
    const searchParams = useMemo(() => {
        return new URLSearchParams(location.search);
    }, [location.search]);

    // Get a specific parameter value
    const getParam = useCallback(
        (key: string): string | null => {
            return searchParams.get(key);
        },
        [searchParams],
    );

    // Update search parameters while preserving current path
    const updateParams = useCallback(
        (updates: Record<string, string | null>) => {
            const newSearchParams = new URLSearchParams(location.search);

            // Apply updates
            for (const [key, value] of Object.entries(updates)) {
                if (value === null || value === '') {
                    newSearchParams.delete(key);
                } else {
                    newSearchParams.set(key, value);
                }
            }

            // Navigate to the same path with updated search params
            const newSearch = newSearchParams.toString();
            const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

            navigate(newUrl);
        },
        [location.pathname, location.search, navigate],
    );

    return {
        searchParams,
        getParam,
        updateParams,
    };
};
